import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import { HiOutlineBell, HiOutlineExclamation } from 'react-icons/hi';

const timeAgo = (d) => {
  if (!d) return '';
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Renewal notification bell for the Super Admin panel.
 *
 * Shows notifications for overdue subscription-renewal tasks that were
 * escalated to the organization's managers (created by
 * services/renewalEscalationService.js in the backend).
 */
const RenewalNotificationBell = () => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // ── Badge + list ──
  const { data: summaryData } = useQuery({
    queryKey: ['renewal-notification-summary'],
    queryFn: superAdminAPI.getRenewalNotificationSummary,
    refetchInterval: 60000,
  });
  const unread = summaryData?.data?.data?.unread ?? 0;

  const { data: listData, isLoading } = useQuery({
    queryKey: ['renewal-notifications'],
    queryFn: () => superAdminAPI.getRenewalNotifications({ limit: 30 }),
    refetchInterval: 60000,
    enabled: isOpen, // fetch when the panel opens
  });
  const notifications = Array.isArray(listData?.data?.data) ? listData.data.data : [];

  // ── Mark all as read ──
  const markReadMutation = useMutation({
    mutationFn: superAdminAPI.markAllRenewalNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-notification-summary'] });
      queryClient.invalidateQueries({ queryKey: ['renewal-notifications'] });
    },
  });

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="relative p-2.5 rounded-xl bg-gray-700/50 border border-gray-600
                   text-gray-200 hover:bg-gray-700 transition-colors"
        title="Renewal notifications"
      >
        <HiOutlineBell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center
                       text-[10px] font-bold rounded-full bg-red-500 text-white shadow"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl
                     bg-white border border-gray-200 overflow-hidden z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">
              Renewal Notifications
              {unread > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-600">
                  {unread} new
                </span>
              )}
            </h3>
            {unread > 0 && (
              <button
                onClick={() => markReadMutation.mutate()}
                disabled={markReadMutation.isLoading}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {isLoading ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">Loading…</p>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <HiOutlineBell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">No notifications</p>
                <p className="text-xs text-gray-400 mt-1">
                  Overdue renewal tasks escalated to managers will appear here.
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n._id}
                  className={`flex gap-3 px-4 py-3 ${n.status === 'open' ? 'bg-red-50/60' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      n.status === 'open' ? 'bg-red-100' : 'bg-gray-100'
                    }`}
                  >
                    <HiOutlineExclamation
                      className={`w-4 h-4 ${n.status === 'open' ? 'text-red-600' : 'text-gray-400'}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900 leading-snug">
                      {n.organizationName}'s renewal task is{' '}
                      <span className="font-semibold">overdue by {n.overdueDays} day{Number(n.overdueDays) !== 1 ? 's' : ''}</span>
                      {Array.isArray(n.escalatedTo) && n.escalatedTo.length > 0 && (
                        <> — escalated to {n.escalatedTo.length} manager{n.escalatedTo.length !== 1 ? 's' : ''}</>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {n.planName ? `${n.planName} · ` : ''}
                      {timeAgo(n.createdAt)}
                      {n.status === 'open' && (
                        <span className="ml-2 text-[10px] font-bold text-red-500">NEW</span>
                      )}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RenewalNotificationBell;